import { memo } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { EarlyWarning } from '@/types/early-warning';

interface DataVisualizationsProps {
  warningData: EarlyWarning[];
}

interface ContentSectionProps {
  title: string;
  children: React.ReactNode;
}

const ContentSection = memo(({ title, children }: ContentSectionProps) => (
  <div className="border-b pb-4 last:border-b-0 last:pb-0 last:pt-4">
    <h2 className="text-base sm:text-lg font-medium mb-2 sm:mb-4">{title}</h2>
    {children}
  </div>
));

ContentSection.displayName = 'ContentSection';

interface MonthData {
  month: string;
  count: number;
  avgScore: number;
  totalScore: number;
}

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: '#3B82F6',
  OVERDUE: '#EF4444',
  AVOIDED: '#10B981',
};

const IMPACT_TYPES = [
  { id: 'increasePrices', name: 'Increase Prices' },
  { id: 'delayCompletion', name: 'Delay Completion' },
  { id: 'delayKeyDate', name: 'Delay Key Date' },
  { id: 'impairPerformance', name: 'Impair Performance' },
  { id: 'increaseContractorCost', name: 'Increase Cost' },
] as const;

export const DataVisualizations = memo(({ warningData }: DataVisualizationsProps) => {
  const statusCounts = {
    ACTIVE: warningData.filter((w) => w.status === 'ACTIVE').length,
    OVERDUE: warningData.filter((w) => w.status === 'OVERDUE').length,
    AVOIDED: warningData.filter((w) => w.status === 'AVOIDED').length,
  };

  const statusData = Object.entries(statusCounts).map(([name, value]) => ({
    name,
    value,
  }));

  const impactData = IMPACT_TYPES.map((impact) => {
    const count = warningData.filter((w) => w.impacts?.[impact.id]).length;
    const percentage =
      warningData.length > 0
        ? Math.round((count / warningData.length) * 100)
        : 0;
    return { name: impact.name, count, percentage };
  });

  const riskMatrixData = [];
  for (let severity = 1; severity <= 5; severity++) {
    for (let likelihood = 1; likelihood <= 5; likelihood++) {
      const score = severity * likelihood;
      const count = warningData.filter(
        (w) => w.severity === severity && w.likelihood === likelihood
      ).length;
      let color = '#F3F4F6';
      if (score >= 15) color = '#FEE2E2';
      else if (score >= 10) color = '#FEF3C7';
      else if (score >= 6) color = '#FEFCE8';
      riskMatrixData.push({ severity, likelihood, score, count, color });
    }
  }

  const warningsByMonth = warningData.reduce<Record<string, MonthData>>(
    (acc, warning) => {
      const date = warning.notificationDate
        ? new Date(warning.notificationDate)
        : new Date();
      const monthYear = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, '0')}`;
      if (!acc[monthYear]) {
        acc[monthYear] = {
          month: date.toLocaleString('default', {
            month: 'short',
            year: '2-digit',
          }),
          count: 0,
          avgScore: 0,
          totalScore: 0,
        };
      }
      acc[monthYear].count += 1;
      acc[monthYear].totalScore += warning.score || 0;
      return acc;
    },
    {}
  );

  const trendData = Object.values(warningsByMonth)
    .map((item) => ({
      ...item,
      avgScore:
        item.count > 0
          ? Math.round((item.totalScore / item.count) * 10) / 10
          : 0,
    }))
    .sort((a, b) => a.month.localeCompare(b.month));

  return (
    <>
      <ContentSection title="Risk Analysis">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-lg border bg-white p-4">
            <h3 className="text-sm font-medium mb-4">Risk Matrix</h3>
            <div className="grid grid-cols-6 gap-1 mb-2">
              <div className="text-right text-xs flex items-center justify-end pr-2 font-medium">
                Likelihood
              </div>
              {[5, 4, 3, 2, 1].map((likelihood) => (
                <div key={`likelihood-row-${likelihood}`} className="contents">
                  <div className="h-10 flex items-center justify-center text-xs font-medium">
                    {likelihood}
                  </div>
                  {[1, 2, 3, 4, 5].map((severity) => {
                    const cell = riskMatrixData.find(
                      (d) =>
                        d.severity === severity && d.likelihood === likelihood
                    );
                    return (
                      <div
                        key={`cell-${likelihood}-${severity}`}
                        className="h-10 border rounded-md flex items-center justify-center relative cursor-pointer transition hover:opacity-90"
                        style={{ backgroundColor: cell?.color }}
                        title={`Likelihood: ${likelihood}, Severity: ${severity}, Risk Score: ${cell?.score}, Count: ${cell?.count}`}
                      >
                        {cell?.count ? (
                          <div className="text-xs font-medium">{cell.count}</div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ))}
              <div key="spacer-1"></div>
              {[1, 2, 3, 4, 5].map((severity) => (
                <div
                  key={`severity-col-${severity}`}
                  className="h-8 flex items-center justify-center text-xs font-medium"
                >
                  {severity}
                </div>
              ))}
              <div key="spacer-2"></div>
              <div
                key="severity-label"
                className="col-span-5 text-center text-xs font-medium"
              >
                Severity
              </div>
            </div>
            <div className="flex justify-center gap-4 mt-4">
              {[
                { color: 'bg-gray-100', label: 'Low (1-5)' },
                { color: 'bg-yellow-50', label: 'Medium (6-9)' },
                { color: 'bg-amber-100', label: 'High (10-14)' },
                { color: 'bg-red-100', label: 'Critical (15+)' },
              ].map((item, index) => (
                <div key={`legend-${index}`} className="flex items-center">
                  <div className={`w-3 h-3 ${item.color} mr-1`}></div>
                  <span className="text-xs">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <h3 className="text-sm font-medium mb-4">
              Warning Status Distribution
            </h3>
            <div className="flex items-center">
              <div className="w-1/2 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {statusData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={STATUS_COLORS[entry.name]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${value} warnings`, 'Count']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2">
                <div className="space-y-3">
                  {statusData.map((status) => (
                    <div
                      key={`status-${status.name}`}
                      className="flex items-center"
                    >
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: STATUS_COLORS[status.name] }}
                      ></div>
                      <div className="text-sm">{status.name}</div>
                      <div className="ml-auto text-sm font-medium">
                        {status.value}
                      </div>
                    </div>
                  ))}
                  <div className="pt-2 border-t">
                    <div className="flex justify-between">
                      <div className="text-sm font-medium">Total:</div>
                      <div className="text-sm font-medium">
                        {warningData.length}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      <ContentSection title="Impact Analysis">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-lg border bg-white p-4">
            <h3 className="text-sm font-medium mb-4">Warning Impact Types</h3>
            <div className="space-y-4">
              {impactData.map((impact) => (
                <div key={`impact-${impact.name}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium">{impact.name}</span>
                    <span>
                      {impact.count} ({impact.percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5">
                    <div
                      className="bg-blue-600 h-2.5 rounded-full"
                      style={{ width: `${impact.percentage}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <h3 className="text-sm font-medium mb-4">Status Summary</h3>
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(statusCounts).map(([status, count]) => {
                const percentage =
                  warningData.length > 0
                    ? Math.round((count / warningData.length) * 100)
                    : 0;
                let bgColor = 'bg-blue-100';
                let textColor = 'text-blue-800';
                if (status === 'OVERDUE') {
                  bgColor = 'bg-red-100';
                  textColor = 'text-red-800';
                } else if (status === 'AVOIDED') {
                  bgColor = 'bg-green-100';
                  textColor = 'text-green-800';
                }
                return (
                  <div
                    key={`summary-${status}`}
                    className={`${bgColor} ${textColor} rounded-lg p-3 text-center`}
                  >
                    <div className="text-2xl font-bold">{count}</div>
                    <div className="text-xs font-medium">
                      {status.toLowerCase()}
                    </div>
                    <div className="text-xs mt-1">({percentage}%)</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6">
              <h4 className="text-xs font-medium mb-2">
                Risk Score Distribution
              </h4>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      {
                        name: 'Low (1-5)',
                        count: warningData.filter((w) => w.score <= 5).length,
                      },
                      {
                        name: 'Medium (6-9)',
                        count: warningData.filter(
                          (w) => w.score > 5 && w.score <= 9
                        ).length,
                      },
                      {
                        name: 'High (10-14)',
                        count: warningData.filter(
                          (w) => w.score > 9 && w.score <= 14
                        ).length,
                      },
                      {
                        name: 'Critical (15+)',
                        count: warningData.filter((w) => w.score >= 15).length,
                      },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3B82F6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      <ContentSection title="Trend Analysis">
        <div className="rounded-lg border bg-white p-4 h-80">
          <h3 className="text-sm font-medium mb-4">Warning Trends Over Time</h3>
          <ResponsiveContainer width="100%" height="85%">
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="count"
                stroke="#3B82F6"
                name="Number of Warnings"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="avgScore"
                stroke="#EF4444"
                name="Avg. Risk Score"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </ContentSection>
    </>
  );
});

DataVisualizations.displayName = 'DataVisualizations';
